import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FolderKanban, MapPin, Plus, UserPlus } from 'lucide-react';
import { DateTime } from 'luxon';
import { PROJECT_STATUSES, type ProjectDto, type ProjectMemberDto, type ProjectStatus } from '@workforce/shared';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useEmployeeOptions } from '@/lib/queries';
import { fmtDate } from '@/lib/format';
import { t } from '@/lib/i18n';
import { Avatar, Badge, Button, Card, ConfirmDialog, EmptyState, ErrorBanner, Field, Input, Modal, PageHeader, Select, Spinner, Textarea } from '@/components/ui';

const STATUS_LABEL: Record<ProjectStatus, string> = { planned: 'Planned', active: 'Active', on_hold: 'On hold', completed: 'Completed' };
const STATUS_TONE: Record<ProjectStatus, 'brand' | 'green' | 'amber' | 'slate'> = { planned: 'brand', active: 'green', on_hold: 'amber', completed: 'slate' };
const today = () => DateTime.now().toISODate()!;

interface ProjectForm {
  id?: string;
  name: string;
  code: string;
  client: string;
  location: string;
  status: ProjectStatus;
  startDate: string;
  endDate: string;
  description: string;
}
const blank: ProjectForm = { name: '', code: '', client: '', location: '', status: 'active', startDate: '', endDate: '', description: '' };
const toForm = (p: ProjectDto): ProjectForm => ({
  id: p.id,
  name: p.name,
  code: p.code ?? '',
  client: p.client ?? '',
  location: p.location ?? '',
  status: p.status,
  startDate: p.startDate ?? '',
  endDate: p.endDate ?? '',
  description: p.description ?? '',
});

function useSaveProject(onDone: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (f: ProjectForm) => {
      const body = {
        name: f.name.trim(),
        code: f.code,
        client: f.client,
        location: f.location,
        status: f.status,
        startDate: f.startDate || null,
        endDate: f.endDate || null,
        description: f.description,
      };
      return f.id ? api.put<ProjectDto>(`/projects/${f.id}`, body) : api.post<ProjectDto>('/projects', body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['employee'] });
      onDone();
    },
  });
}

function ProjectModal({ form, setForm, onClose }: { form: ProjectForm | null; setForm: (f: ProjectForm) => void; onClose: () => void }) {
  const save = useSaveProject(onClose);
  const fe = save.error instanceof ApiError ? save.error.fieldErrors() : {};
  const set = <K extends keyof ProjectForm>(k: K, v: ProjectForm[K]) => form && setForm({ ...form, [k]: v });
  return (
    <Modal
      open={!!form}
      onClose={onClose}
      title={form?.id ? t('Edit project') : t('New project')}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button loading={save.isPending} disabled={!form?.name.trim()} onClick={() => form && save.mutate(form)}>
            {t('Save')}
          </Button>
        </>
      }
    >
      {form && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="sm:col-span-3">
            <ErrorBanner error={save.error} />
          </div>
          <Field label={t('Project name')} required error={fe.name} className="sm:col-span-2">
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder={t('e.g. Riyadh warehouse')} />
          </Field>
          <Field label={t('Code')} error={fe.code} hint={t('Optional')}>
            <Input value={form.code} onChange={(e) => set('code', e.target.value)} maxLength={30} />
          </Field>
          <Field label={t('Client')} error={fe.client}>
            <Input value={form.client} onChange={(e) => set('client', e.target.value)} />
          </Field>
          <Field label={t('Location')} error={fe.location}>
            <Input value={form.location} onChange={(e) => set('location', e.target.value)} />
          </Field>
          <Field label={t('Status')}>
            <Select value={form.status} onChange={(e) => set('status', e.target.value as ProjectStatus)}>
              {PROJECT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(STATUS_LABEL[s])}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('Start date')} error={fe.startDate}>
            <Input type="date" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />
          </Field>
          <Field label={t('End date')} error={fe.endDate}>
            <Input type="date" value={form.endDate} onChange={(e) => set('endDate', e.target.value)} />
          </Field>
          <Field label={t('Description')} className="sm:col-span-3">
            <Textarea rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} maxLength={2000} />
          </Field>
        </div>
      )}
    </Modal>
  );
}

export function ProjectsPage() {
  const { can } = useAuth();
  const q = useQuery({ queryKey: ['projects'], queryFn: () => api.get<ProjectDto[]>('/projects') });
  const [form, setForm] = useState<ProjectForm | null>(null);
  const [filter, setFilter] = useState<'open' | 'all'>('open');
  const shown = (q.data ?? []).filter((p) => filter === 'all' || p.status !== 'completed');
  return (
    <div>
      <PageHeader
        title={t('Projects')}
        subtitle={t('Which engineers work on which project. An engineer can be on several projects.')}
        actions={
          can('projects.write') && (
            <Button icon={<Plus className="h-4 w-4" />} onClick={() => setForm({ ...blank })}>
              {t('New project')}
            </Button>
          )
        }
      />
      <div className="mb-4 flex gap-2">
        <Select value={filter} onChange={(e) => setFilter(e.target.value as 'open' | 'all')} className="w-auto">
          <option value="open">{t('Current projects')}</option>
          <option value="all">{t('All projects, including completed')}</option>
        </Select>
      </div>
      <ErrorBanner error={q.error} />
      {q.isLoading ? (
        <Spinner />
      ) : !shown.length ? (
        <Card>
          <EmptyState icon={<FolderKanban className="h-8 w-8" />} title={t('No projects yet')} description={can('projects.write') ? t('Create a project, then add the engineers who work on it.') : undefined} />
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {shown.map((p) => (
            <Link key={p.id} to={`/projects/${p.id}`} className="card block p-4 transition-shadow hover:shadow-md">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-slate-900">{p.name}</p>
                  <p className="truncate text-xs text-slate-500">{[p.code, p.client].filter(Boolean).join(' · ') || '—'}</p>
                </div>
                <Badge tone={STATUS_TONE[p.status]}>{t(STATUS_LABEL[p.status])}</Badge>
              </div>
              {p.location && (
                <p className="mt-2 flex items-center gap-1 text-xs text-slate-500">
                  <MapPin className="h-3.5 w-3.5" /> {p.location}
                </p>
              )}
              <div className="mt-3 flex items-center justify-between">
                <div className="flex -space-x-2 rtl:space-x-reverse">
                  {p.members
                    .filter((m) => m.current)
                    .slice(0, 5)
                    .map((m) => (
                      <span key={m.id} className="rounded-full ring-2 ring-surface">
                        <Avatar name={m.employeeName} size="sm" />
                      </span>
                    ))}
                </div>
                <span className="text-xs text-slate-500">{p.memberCount === 1 ? t('1 person') : t('{n} people', { n: p.memberCount })}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
      <ProjectModal form={form} setForm={setForm} onClose={() => setForm(null)} />
    </div>
  );
}

export function ProjectDetailPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['projects', id], queryFn: () => api.get<ProjectDto>(`/projects/${id}`) });
  const people = useEmployeeOptions(can('projects.write'));
  const [form, setForm] = useState<ProjectForm | null>(null);
  const [adding, setAdding] = useState<{ employeeId: string; role: string; fromDate: string } | null>(null);
  const [ending, setEnding] = useState<{ m: ProjectMemberDto; toDate: string } | null>(null);
  const [removing, setRemoving] = useState<ProjectMemberDto | null>(null);
  const [deleting, setDeleting] = useState(false);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['projects'] });
    void qc.invalidateQueries({ queryKey: ['employee'] });
  };
  const add = useMutation({
    mutationFn: () => api.post<ProjectDto>(`/projects/${id}/members`, { employeeId: adding!.employeeId, role: adding!.role, fromDate: adding!.fromDate }),
    onSuccess: () => {
      setAdding(null);
      refresh();
    },
  });
  const end = useMutation({
    mutationFn: () => api.post<ProjectDto>(`/projects/${id}/members/${ending!.m.id}/end`, { toDate: ending!.toDate }),
    onSuccess: () => {
      setEnding(null);
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (memberId: string) => api.del<ProjectDto>(`/projects/${id}/members/${memberId}`),
    onSuccess: () => {
      setRemoving(null);
      refresh();
    },
  });
  const del = useMutation({
    mutationFn: () => api.del(`/projects/${id}`),
    onSuccess: () => {
      refresh();
      window.history.back();
    },
  });
  const addErr = add.error instanceof ApiError ? add.error.fieldErrors() : {};

  if (q.isLoading) return <Spinner />;
  if (q.error || !q.data) return <ErrorBanner error={q.error ?? new Error(t('Project not found'))} />;
  const p = q.data;
  const current = p.members.filter((m) => m.current);
  const past = p.members.filter((m) => !m.current);
  const onProject = new Set(current.map((m) => m.employeeId));

  return (
    <div>
      <Link to="/projects" className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4 rtl:rotate-180" /> {t('Projects')}
      </Link>
      <PageHeader
        title={p.name}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={STATUS_TONE[p.status]}>{t(STATUS_LABEL[p.status])}</Badge>
            {[p.code, p.client, p.location].filter(Boolean).join(' · ')}
          </span>
        }
        actions={
          can('projects.write') && (
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setForm(toForm(p))}>
                {t('Edit')}
              </Button>
              {!p.members.length && (
                <Button variant="ghost" onClick={() => setDeleting(true)}>
                  {t('Delete')}
                </Button>
              )}
            </div>
          )
        }
      />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card
          className="lg:col-span-2"
          title={t('On the project ({n})', { n: current.length })}
          actions={
            can('projects.write') && (
              <Button size="sm" icon={<UserPlus className="h-4 w-4" />} onClick={() => { add.reset(); setAdding({ employeeId: '', role: '', fromDate: today() }); }}>
                {t('Add person')}
              </Button>
            )
          }
          bodyClass="p-0"
        >
          {!current.length ? (
            <EmptyState title={t('Nobody on this project yet')} />
          ) : (
            <ul className="divide-y divide-slate-100">
              {current.map((m) => (
                <li key={m.id} className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-center">
                  <Link to={`/employees/${m.employeeId}`} className="flex min-w-0 flex-1 items-center gap-3 hover:text-brand-700">
                    <Avatar name={m.employeeName} size="sm" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{m.employeeName}</p>
                      <p className="truncate text-xs text-slate-500">
                        {[m.role ?? m.position, t('since {date}', { date: fmtDate(m.fromDate) })].filter(Boolean).join(' · ')}
                        {m.toDate ? ` · ${t('until {date}', { date: fmtDate(m.toDate) })}` : ''}
                      </p>
                    </div>
                  </Link>
                  {can('projects.write') && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => { end.reset(); setEnding({ m, toDate: today() }); }}>
                        {t('Take off project')}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setRemoving(m)}>
                        {t('Remove')}
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
        <div className="space-y-6">
          <Card title={t('Details')}>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">{t('Start date')}</dt>
                <dd>{fmtDate(p.startDate)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">{t('End date')}</dt>
                <dd>{fmtDate(p.endDate)}</dd>
              </div>
            </dl>
            {p.description && <p className="mt-3 whitespace-pre-line text-sm text-slate-700">{p.description}</p>}
          </Card>
          {past.length > 0 && (
            <Card title={t('Previously on the project')}>
              <ul className="space-y-2 text-sm">
                {past.map((m) => (
                  <li key={m.id}>
                    <Link to={`/employees/${m.employeeId}`} className="font-medium hover:text-brand-700">
                      {m.employeeName}
                    </Link>
                    <p className="text-xs text-slate-500">
                      {fmtDate(m.fromDate)} → {fmtDate(m.toDate)}
                      {m.role ? ` · ${m.role}` : ''}
                    </p>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      </div>

      <ProjectModal form={form} setForm={setForm} onClose={() => setForm(null)} />
      <Modal
        open={!!adding}
        onClose={() => setAdding(null)}
        title={t('Add person to {name}', { name: p.name })}
        footer={
          <>
            <Button variant="secondary" onClick={() => setAdding(null)}>
              {t('Cancel')}
            </Button>
            <Button loading={add.isPending} disabled={!adding?.employeeId || !adding.fromDate} onClick={() => add.mutate()}>
              {t('Add')}
            </Button>
          </>
        }
      >
        {adding && (
          <div className="space-y-4">
            <ErrorBanner error={add.error} />
            <Field label={t('Employee')} required error={addErr.employeeId}>
              <Select value={adding.employeeId} onChange={(e) => setAdding({ ...adding, employeeId: e.target.value })}>
                <option value="">{t('Select…')}</option>
                {(people.data ?? [])
                  .filter((x) => !onProject.has(x.id))
                  .map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.fullName} ({x.employeeCode})
                    </option>
                  ))}
              </Select>
            </Field>
            <Field label={t('Role on the project')} hint={t('Optional, e.g. Structural engineer')}>
              <Input value={adding.role} onChange={(e) => setAdding({ ...adding, role: e.target.value })} maxLength={80} />
            </Field>
            <Field label={t('From')} required error={addErr.fromDate}>
              <Input type="date" value={adding.fromDate} onChange={(e) => setAdding({ ...adding, fromDate: e.target.value })} />
            </Field>
          </div>
        )}
      </Modal>
      <Modal
        open={!!ending}
        onClose={() => setEnding(null)}
        title={t('Take {name} off the project', { name: ending?.m.employeeName ?? '' })}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEnding(null)}>
              {t('Cancel')}
            </Button>
            <Button loading={end.isPending} disabled={!ending?.toDate} onClick={() => end.mutate()}>
              {t('Save')}
            </Button>
          </>
        }
      >
        {ending && (
          <div className="space-y-4">
            <ErrorBanner error={end.error} />
            <Field label={t('Last day on the project')} required hint={t('The history is kept.')}>
              <Input type="date" value={ending.toDate} min={ending.m.fromDate} onChange={(e) => setEnding({ ...ending, toDate: e.target.value })} />
            </Field>
          </div>
        )}
      </Modal>
      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        title={t('Remove {name}?', { name: removing?.employeeName ?? '' })}
        message={t('Use this only to correct a mistake — to keep the history, use “Take off project” instead.')}
        confirmLabel={t('Remove')}
        danger
        loading={remove.isPending}
        error={remove.error}
        onConfirm={() => removing && remove.mutate(removing.id)}
      />
      <ConfirmDialog
        open={deleting}
        onClose={() => setDeleting(false)}
        title={t('Delete {name}?', { name: p.name })}
        message={t('This project has never had anyone on it, so it can be deleted permanently.')}
        confirmLabel={t('Delete')}
        danger
        loading={del.isPending}
        error={del.error}
        onConfirm={() => del.mutate()}
      />
    </div>
  );
}
